import React from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  CircularProgress,
  Box,
  Button,
  Typography,
} from '@mui/material'
import { alpha, Theme } from '@mui/material/styles'
import EditIcon from '@mui/icons-material/Edit'
import ArchiveIcon from '@mui/icons-material/Archive'
import PreviewIcon from '@mui/icons-material/Preview'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import { FormTemplate } from '@/core/models/form.model'
import Pagination from '@/components/ui/pagination/Pagination'
import { t } from '@lingui/macro'
import { useLanguage } from '@/hooks/useLanguage'
import { getCategoryLabel, getTagLabel } from '../vocabularyLabels'

interface FormTemplatesListProps {
  templates: FormTemplate[]
  totalCount: number
  page: number
  pageSize: number
  isLoading?: boolean
  onEdit: (template: FormTemplate) => void
  onArchive: (template: FormTemplate) => void
  onPreview: (template: FormTemplate) => void
  onView: (template: FormTemplate) => void
  onCreateNew: () => void
  onPageChange: (page: number, itemsPerPage: number) => void
  onPageSizeChange: (pageSize: number) => void
}

const headCellSx = {
  bgcolor: 'grey.100',
  color: 'text.secondary',
  fontWeight: 600,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  borderBottom: 1,
  borderColor: 'divider',
  py: 1.75,
} as const

const chipBaseSx = {
  height: 26,
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '0.8rem',
  '& .MuiChip-label': { px: 1.25 },
} as const

const categoryChipSx = (theme: Theme) => ({
  ...chipBaseSx,
  bgcolor: alpha(theme.palette.primary.main, 0.14),
  color: theme.palette.primary.dark,
})

const tagChipSx = (theme: Theme) => ({
  ...chipBaseSx,
  bgcolor: alpha(theme.palette.warning.main, 0.16),
  color: theme.palette.warning.dark,
})

const actionBtnSx = { width: 32, height: 32, borderRadius: 1.5, flexShrink: 0 } as const

function StatusBadge({ active }: { active: boolean }) {
  return (
    <Box
      component="span"
      sx={(theme) => {
        const main = active ? theme.palette.success.main : theme.palette.grey[500]
        const text = active ? theme.palette.success.dark : theme.palette.grey[700]
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.625,
          borderRadius: 999,
          fontSize: '0.8rem',
          fontWeight: 600,
          bgcolor: alpha(main, 0.14),
          color: text,
          border: 1,
          borderColor: alpha(main, 0.4),
        }
      }}
    >
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: active ? 'success.main' : 'grey.500' }} />
      {active ? t`Active` : t`Archived`}
    </Box>
  )
}

const FormTemplatesListComponent: React.FC<FormTemplatesListProps> = ({
  templates,
  totalCount,
  page,
  pageSize,
  isLoading,
  onEdit,
  onArchive,
  onPreview,
  onView,
  onCreateNew,
  onPageChange,
  onPageSizeChange,
}) => {
  const lang = useLanguage()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress color="primary" />
      </Box>
    )
  }

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onCreateNew}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            py: 1.25,
            transition: 'transform 0.2s ease',
            '&:hover': { transform: 'scale(0.98)' },
          }}
        >
          {t`Create new template`}
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
          boxShadow: 3,
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: '20%' }}>{t`Title`}</TableCell>
                <TableCell sx={{ ...headCellSx, width: '28%' }}>{t`Description`}</TableCell>
                <TableCell sx={headCellSx}>{t`Status`}</TableCell>
                <TableCell sx={{ ...headCellSx, width: '14%' }}>{t`Categories`}</TableCell>
                <TableCell sx={{ ...headCellSx, width: '16%' }}>{t`Tags`}</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: 'center' }}>{t`Fields`}</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>{t`Actions`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow
                  key={template._id}
                  sx={{
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: 'action.hover' },
                    '& td': { borderBottom: 1, borderColor: 'divider', py: 1.75 },
                    '&:last-of-type td': { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: 'info.main', fontSize: '0.95rem', lineHeight: 1.35 }}>{template.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.9rem',
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        whiteSpace: 'normal',
                      }}
                    >
                      {template.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusBadge active={template.isActive} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {template.categories?.map((category) => (
                        <Chip key={category} label={getCategoryLabel(category, lang)} sx={categoryChipSx} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {template.tags?.map((tag) => (
                        <Chip key={tag} label={getTagLabel(tag, lang)} sx={tagChipSx} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {template.fields.length}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Tooltip title={t`View`}>
                        <IconButton color="primary" onClick={() => onView(template)} sx={actionBtnSx}>
                          <VisibilityIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t`Preview`}>
                        <IconButton color="info" onClick={() => onPreview(template)} sx={actionBtnSx}>
                          <PreviewIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      {template.isActive && (
                        <>
                          <Tooltip title={t`Edit`}>
                            <IconButton color="secondary" onClick={() => onEdit(template)} sx={actionBtnSx}>
                              <EditIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t`Archive`}>
                            <IconButton
                              color="warning"
                              onClick={() => onArchive(template)}
                              sx={{
                                ...actionBtnSx,
                                bgcolor: 'warning.main',
                                color: 'common.white',
                                transition: 'transform 0.15s ease',
                                '&:hover': { bgcolor: 'warning.dark', transform: 'scale(1.06)' },
                              }}
                            >
                              <ArchiveIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Pagination
            totalItems={totalCount}
            page={page}
            onPageChange={onPageChange}
            onItemsPerPageChange={onPageSizeChange}
            defaultItemsPerPage={pageSize}
            itemsPerPageOptions={[5, 10, 25]}
          />
        </Box>
      </Paper>
    </Stack>
  )
}

export default FormTemplatesListComponent
